import Konva from 'konva';
import axios from '../axios';

export declare interface CreateRequestResp {
    userEventID: string;
}

export declare interface CreateMultipleRequestResp {
    userEventIDs: string[];
}

export declare interface UserEventRequest {
    quoteProofID: string;
    notes: string;
    uploadIDs: number[];
    point?: point;
    pointNumber?: number;
}

export async function CreateRequest(quoteHash: string, quoteProofID: string, notes: string, uploadIDs: number[], point?: point, pointNumber?: number) {
    return axios.post<CreateRequestResp>(`/quotes/${quoteHash}/revisions`, {
        quoteProofID,
        notes,
        uploadIDs,
        point,
        pointNumber,
    });
}

export async function CreateMultipleRequests(quoteHash: string, userEvents: UserEventRequest[]) {
    return axios.post<CreateMultipleRequestResp>(`/quotes/${quoteHash}/revisions`, {
        'revisionRequests': userEvents,
    });
}

export async function DeleteRequest(quoteHash: string, userEventID: string) {
    return axios.delete<void>(`/quotes/${quoteHash}/revisions/${userEventID}`);
}

export async function RestoreRequest(quoteHash: string, userEventID: string) {
    return axios.patch<void>(`/quotes/${quoteHash}/revisions/${userEventID}/restore`);
}

declare interface createUploadsResp {
    files: file[];
}

declare interface file {
    uploadID: number;
    url: string;
    name: string;
    mime: string;
}

export declare interface RevisionOpts {
    imageURL: string;
    container: HTMLElement;
    proofID: string;
    primaryColor?: string;
    strokeColor?: string;
    textColor?: string;
    bold?: boolean;
    shadowBlur?: number;
}

declare interface point {
    x: number;
    y: number;
}

export declare interface Storage {
    markers: Record<number, point>;
    notes: Record<number, string>;
}

// TODO: make it so that the state of the revision
// is only controlled by marker point
//
// too much control is added to the user
// and therefore the user can make a mistake and mess up the state.
//
// a user should on be able to interact
// - when a marker is added
// - when a marker is removed
// - when comments are changed
// - removing all markers (which removes all notes)
// - submitting the revision request
export class Revision {
    private container: HTMLElement;
    private stage?: Konva.Stage;
    private layer?: Konva.Layer;
    private proof?: Konva.Image;

    private originalProofHeight: number = 0;
    private originalProofWidth: number = 0;

    private heightRatio: number = 0;
    private widthRatio: number = 0;

    private ratio: number = 1;

    private virtualWidth: number = 900;
    private virtualHeight: number = 900;

    private containerWidth: number;
    private containerHeight: number;

    ProofID: string;
    StorageKey: string;
    private storageObject: Storage = {
        markers: {},
        notes: {},
    };

    PrimaryColor: string;
    StrokeColor: string;
    TextColor: string;

    Bold: boolean = true;
    ShadowBlur: number = 0;

    CurrentRevisionComment: number = 0;

    private markerInFocus: number = 0;

    OnMarkerAdd?: () => void;
    OnMarkerRemove?: (n: number) => void;
    OnMarkerRenumber?: (n: number) => void;

    SubmittedMarkerNumbers: number[] = [];

    constructor(opts: RevisionOpts) {
        this.container = opts.container;
        this.ProofID = opts.proofID;
        this.StorageKey = `${this.ProofID}-revision`;

        const ratio = this.container.clientWidth / this.virtualWidth;

        if (ratio > 0 && ratio < 1) {
            this.ratio = ratio;
        } else if (ratio >= 1) {
            this.ratio = 1;
        }

        if (typeof opts.bold !== 'undefined') {
            this.Bold = opts.bold;
        }

        if (opts.shadowBlur) {
            this.ShadowBlur = opts.shadowBlur;
        }

        this.StrokeColor = opts.strokeColor || '';
        this.TextColor = opts.textColor || 'white';

        this.containerWidth = 900;
        this.containerHeight = 900;

        this.stage = new Konva.Stage({
            container: this.container as HTMLDivElement,
            height: this.containerHeight,
            width: this.containerWidth,
            offset: {
                x: -(this.containerWidth / 2),
                y: -(this.containerHeight / 2),
            },
        });

        const imageLayer: Konva.Layer = new Konva.Layer();
        this.stage.add(imageLayer);

        this.layer = new Konva.Layer();
        this.stage.add(this.layer);

        const proof = new Image();

        proof.crossOrigin = 'Anonymous';
        proof.src = opts.imageURL;

        // need to fit the stage to the image
        proof.onload = () => {
            this.originalProofHeight = proof.height;
            this.originalProofWidth = proof.width;

            this.widthRatio = this.containerWidth / this.originalProofWidth;
            this.heightRatio = this.containerHeight / this.originalProofHeight;

            this.ratio = this.widthRatio < this.heightRatio ? this.widthRatio : this.heightRatio;

            const xBigger: boolean = this.originalProofWidth > this.originalProofHeight;

            this.proof = new Konva.Image({
                image: proof,
                x: xBigger ? -(this.containerWidth / 2) : -(proof.width * this.ratio / 2),
                y: xBigger ? -(this.containerHeight * this.ratio / 2) : -(this.containerHeight / 2),
                width: proof.width,
                height: proof.height,
                scale: {
                    x: this.ratio,
                    y: this.ratio,
                },
            });

            imageLayer.add(this.proof);

            this.proof.on('click', () => {
                if (!this.stage) {
                    throw new Error('stage is undefined');
                }
                const pos = this.stage.getRelativePointerPosition();
                if (pos !== null) {
                    this.AddMarker(pos, false);
                }
            });
            this.proof.on('tap', () => {
                if (!this.stage) {
                    throw new Error('stage is undefined');
                }
                const pos = this.stage.getRelativePointerPosition();
                if (pos !== null) {
                    this.AddMarker(pos, false);
                }
            });
        };

        this.PrimaryColor = opts.primaryColor || 'red';

        this.container.style.cursor = 'crosshair';

        this.resize();
        window.addEventListener('resize', () => this.resize());
    }

    private setMarkerLocalStorage(p: point, pointNumber: number) {
        const storage = window.localStorage.getItem(this.StorageKey);
        if (storage?.length) {
            this.storageObject = JSON.parse(storage) as Storage;
        }
        this.storageObject.markers[pointNumber] = p;
        window.localStorage.setItem(this.StorageKey, JSON.stringify(this.storageObject));
    }

    private removeMarkerLocalStorage(pointNumber: number) {
        const storage = window.localStorage.getItem(this.StorageKey);
        if (storage?.length) {
            this.storageObject = JSON.parse(storage) as Storage;
        }
        delete this.storageObject.markers[pointNumber];
        window.localStorage.setItem(this.StorageKey, JSON.stringify(this.storageObject));
    }

    private reorderLocalStorage() {
        const storage = window.localStorage.getItem(this.StorageKey);
        if (storage?.length) {
            this.storageObject = JSON.parse(storage) as Storage;
        }

        // TODO deal with both markers and notes in a single loop, should
        // be fairly striaghtforward as they should at this point
        // have the same number of elements and removed
        let num = 0;
        for (const k of Object.keys(this.storageObject.markers)) {
            num++;
            const n = parseInt(k, 10);
            if (n === num) {
                continue;
            }

            const p = this.storageObject.markers[n];
            delete this.storageObject.markers[n];
            this.storageObject.markers[num] = p;
        }

        num = 0;
        for (const k of Object.keys(this.storageObject.notes)) {
            num++;
            const n = parseInt(k, 10);
            if (n === num) {
                continue;
            }

            const p = this.storageObject.notes[n];
            delete this.storageObject.notes[n];
            this.storageObject.notes[num] = p;
        }

        window.localStorage.setItem(this.StorageKey, JSON.stringify(this.storageObject));
    }

    AddMarker(p: point, old: boolean, pointNumber?: number) {
        this.CurrentRevisionComment++;

        // get current stage scale
        const scale = this.stage?.scaleX();

        const marker = new Konva.Group({
            id: `revision-marker-${pointNumber || this.CurrentRevisionComment}`,
            'revisionNumber': pointNumber || this.CurrentRevisionComment, // stringified key so closure compiler doesn't eat it up, custom key
            x: p.x,
            y: p.y,
            scale: {
                x: scale === 1 ? 1 : 1 / this.ratio, // stage scale does not change until a resize occurs, make the marker add normal until rescale
                y: scale === 1 ? 1 : 1 / this.ratio,
            },
            draggable: !old,
        });

        const markerCircle = new Konva.Circle({
            radius: 12,
            fill: this.PrimaryColor,
            stroke: this.StrokeColor,
            shadowBlur: this.ShadowBlur,
        });

        const markerText = new Konva.Text({
            text: pointNumber?.toString() || this.CurrentRevisionComment.toString(),
            fill: this.TextColor,
            fontSize: 16,
            fontStyle: this.Bold ? '900' : 'normal',
        });

        marker.add(markerCircle);
        marker.add(markerText);

        const width = markerText.width();
        const height = markerText.height();

        if (!this.layer) {
            throw new Error('layer is undefined');
        }
        this.layer.add(marker);

        // always center text
        // this is done later because we dont
        // know the size until its on the canvas
        //
        // minus one to the height because it looks weird when
        // it isn't there
        markerText.position({
            x: -width / 2,
            y: -(height - 1) / 2,
        });

        marker.on('dragmove', () => {
            const p = marker.getPosition();
            const dim = {
                width: markerCircle.width(),
                height: markerCircle.height(),
            };

            if (this.proof) {
                const proofPosition = this.proof.getPosition();

                // some fun math here lol
                // TODO: maybe make this more clear why this works
                if (p.x - ((dim.width / 2) * this.ratio) < proofPosition.x) {
                    marker.x(proofPosition.x + ((dim.width / 2) * this.ratio));
                } else if (p.x + ((dim.width / 2) * this.ratio) > proofPosition.x + (this.proof.width() * this.ratio)) {
                    marker.x(proofPosition.x + (this.proof.width() * this.ratio) - ((dim.width / 2) * this.ratio));
                }

                if (p.y - (dim.height / 2) < proofPosition.y) {
                    marker.y(proofPosition.y + (dim.height / 2));
                } else if (p.y + ((dim.height / 2) * this.ratio) > proofPosition.y + (this.proof.height() * this.ratio)) {
                    marker.y(proofPosition.y + (this.proof.height() * this.ratio) - ((dim.height / 2) * this.ratio));
                }
            }
        });

        if (this.OnMarkerAdd && !old) {
            this.OnMarkerAdd();
        }

        // cursor updates to signal that the user can
        // interact with the markers
        //
        // older / already submitted markers cannot be deleted
        // from the canvas side of things
        const temp: number = pointNumber || this.CurrentRevisionComment;
        if (!old) {
            marker.on('mouseover', () => this.container.style.cursor = 'pointer');
            marker.on('mouseleave', () => this.container.style.cursor = 'crosshair');
            marker.on('dragstart', () => this.container.style.cursor = 'grabbing');
            marker.on('dragend', () => {
                this.container.style.cursor = 'pointer';
                this.removeMarkerLocalStorage(temp);
                this.setMarkerLocalStorage(marker.position(), temp);
            });
            this.setMarkerLocalStorage(p, temp);
        } else {
            this.SubmittedMarkerNumbers.push(temp);
        }
    }

    RemoveMarker(marker: number) {
        if (!this.stage) {
            throw new Error('stage is undefined');
        }
        const m = this.stage.find(`#revision-marker-${marker}`);

        if (!m.length) {
            return;
        }

        m[0].destroy();

        if (this.OnMarkerRemove) {
            this.OnMarkerRemove(marker);
        }

        this.container.style.cursor = 'crosshair';

        this.CurrentRevisionComment--;

        const i = this.SubmittedMarkerNumbers.indexOf(marker);
        if (i !== -1) {
            this.SubmittedMarkerNumbers.splice(i, 1);
        }

        this.removeMarkerLocalStorage(marker);
        this.renumberIDs(marker);
    }

    RemoveAllMarkers() {
        if (!this.stage) {
            throw new Error('stage is undefined');
        }
        const m = this.stage.find('Group');

        if (!m.length) {
            return;
        }

        for (const marker of m) {
            this.RemoveMarker(Number(marker.getAttr('revisionNumber')));
            this.RemoveNotes(Number(marker.getAttr('revisionNumber')));
        }
    }

    MarkerFocus(marker: number) {
        if (!this.stage) {
            throw new Error('stage is undefined');
        }
        const markers = this.stage.find('Group');

        if (!markers.length || marker === 0) {
            return;
        }

        this.markerInFocus = marker;

        for (const m of markers) {
            if (m.id() === `revision-marker-${marker}`) {
                continue;
            }

            m.opacity(0.25);
        }
    }

    MarkerOffFocus() {
        if (!this.stage) {
            throw new Error('stage is undefined');
        }
        const markers = this.stage.find('Group');

        if (!markers.length) {
            return;
        }

        this.markerInFocus = 0;

        for (const m of markers) {
            m.opacity(1);
        }
    }

    private resize() {
        const ratio = this.container.clientWidth / this.virtualWidth;

        if (ratio > 0 && ratio < 1) {
            this.ratio = ratio;
        } else if (ratio >= 1) {
            this.ratio = 1;
        }

        const w = this.ratio * this.virtualWidth;
        const h = this.ratio * this.virtualHeight;
        this.stage?.width(w);
        this.stage?.height(h);

        this.stage?.scale({
            x: this.ratio,
            y: this.ratio,
        });

        // scale marker opposite to ratio
        // ratio of 1 should be max size anyways
        const m = this.stage?.find('Group');
        m?.forEach(n => {
            n.scale({
                x: 1 / this.ratio,
                y: 1 / this.ratio,
            });
        });
    }

    // renumberIDs reorganizes the ids after a delete state change.
    // this goes through the markers and makes sure that the numbers
    // are some integer going consistently up with no gaps in the integer.
    private renumberIDs(marker: number) {
        if (!this.stage) {
            throw new Error('stage is undefined');
        }
        const groups = this.stage.find('Group') as Konva.Group[];
        let currentNumber = 0;

        for (const group of groups) {
            currentNumber++;
            const n = Number(group.getAttr('revisionNumber'));

            if (n === currentNumber) {
                continue;
            } else if (n > currentNumber) {
                group.setAttr('revisionNumber', currentNumber);
                group.id(`revision-marker-${currentNumber}`);

                const nodes = group.getChildren();
                for (const node of nodes) {
                    if (node.className === 'Text') {
                        const t = node as Konva.Text;
                        t.text(currentNumber.toString());
                    }
                }
                group.on('dragend', () => {
                    this.container.style.cursor = 'pointer';
                    this.removeMarkerLocalStorage(n);
                    this.setMarkerLocalStorage(group.position(), currentNumber);
                });
            }
        }

        if (this.OnMarkerRenumber) {
            this.OnMarkerRenumber(marker);
        }
        this.reorderLocalStorage();
    }

    AddNotes(pointNumber: number, text: string) {
        const storage = window.localStorage.getItem(this.StorageKey);
        if (storage?.length) {
            this.storageObject = JSON.parse(storage) as Storage;
        }
        this.storageObject.notes[pointNumber] = text;
        window.localStorage.setItem(this.StorageKey, JSON.stringify(this.storageObject));
    }

    RemoveNotes(pointNumber: number) {
        const storage = window.localStorage.getItem(this.StorageKey);
        if (storage?.length) {
            this.storageObject = JSON.parse(storage) as Storage;
        }
        delete this.storageObject.notes[pointNumber];
        window.localStorage.setItem(this.StorageKey, JSON.stringify(this.storageObject));
    }

    async GetRevisionImage(proofNumber: string): Promise<number[]> {
        const uploadIDs: number[] = [];

        this.stage?.height(this.virtualHeight);
        this.stage?.width(this.virtualWidth);

        this.stage?.scale({
            x: 1,
            y: 1,
        });

        const markers = this.stage?.find('Group');
        markers?.forEach(n => {
            n.scale({
                x: 1,
                y: 1,
            });
        });
        this.MarkerOffFocus();

        if (!this.stage) {
            throw new Error('stage is undefined');
        }
        const dataURL = this.stage.toDataURL({ mimeType: 'image/png' });
        const b = dataURItoBlob(dataURL);

        this.resize();
        this.MarkerFocus(this.markerInFocus);

        await axios.post<createUploadsResp>('/uploads', {
            files: [{
                name: `proof-${proofNumber}-revision.png`,
                mime: 'image/png',
            }],
        }).then(async r => {
            const resp = r.data;

            uploadIDs.push(resp.files[0].uploadID);
            await axios.put(resp.files[0].url, b, {
                headers: {
                    'content-type': 'binary/octet-stream',
                },
            });
        });

        return uploadIDs;
    }

    GetMarkerPoint(pointNumber: number): { x: number; y: number } | undefined {
        if (!this.layer) {
            throw new Error('layer is undefined');
        }
        const p = this.layer.find(`#revision-marker-${pointNumber}`);

        if (p.length !== 1) {
            return;
        }

        return p[0].position();
    }
}

function dataURItoBlob(dataURI: string) {
    const binary = atob(dataURI.split(',')[1]);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: 'image/png' });
}